import type { DropDownListChangeEvent } from "@tql/ui-components-react/kendo/dropdowns";
import type { TextBoxChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import { Input, TextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { useMemo } from "react";

import type { HazmatClass, HazmatGroup } from "~/shared/schemas/hazmat-schema";
import type {
	HandlingUnit,
	OnChange,
} from "~/shared/types/quote-details-types";

type FreightHazmatFieldsProps = {
	handlingUnit: HandlingUnit;
	hazmatClasses: HazmatClass[] | undefined;
	hazmatGroups: HazmatGroup[] | undefined;
	onChange: (field: keyof HandlingUnit, value: OnChange | null) => void;
	validationErrors?: Set<string>;
};

const RequiredMark = () => <span className={"required"}>&nbsp;*</span>;

/**
 * Prop-driven hazmat fields (UN number, proper shipping name, hazmat class,
 * packing group) for the shipment freight editor. Mirrors the quote flow's
 * `HazmatDetailRow`, but routes edits through the `onChange` prop instead of
 * `QuoteDetailsContext`, which isn't available in shipment management. Styled
 * by the edit commodity modal to match its handling-unit field row.
 */
export const FreightHazmatFields = ({
	handlingUnit,
	hazmatClasses,
	hazmatGroups,
	onChange,
	validationErrors,
}: FreightHazmatFieldsProps) => {
	const displayHazmatGroups: HazmatGroup[] | undefined = useMemo(
		() =>
			hazmatGroups?.map((g) => ({
				...g,
				displayCode: g.code === "None" ? "N/A" : g.code,
			})),
		[hazmatGroups],
	);

	const unNumberError = validationErrors?.has("unNumber") ?? false;
	const properShippingNameError =
		validationErrors?.has("properShippingName") ?? false;
	const hazmatClassError = validationErrors?.has("hazmatClass") ?? false;
	const packingGroupError = validationErrors?.has("packingGroupId") ?? false;

	return (
		<div
			className={"freight-hazmat-fields"}
			data-testid={`freight-hazmat-fields-${handlingUnit.id}`}
		>
			<div className={"freight-hazmat-field freight-hazmat-field-un-number"}>
				<Label editorId={`un-number-${handlingUnit.id}`}>
					UN Number
					<RequiredMark />
				</Label>
				<TextBox
					id={`un-number-${handlingUnit.id}`}
					onChange={(e: TextBoxChangeEvent) => {
						onChange("unNumber", e.value as string);
					}}
					valid={!unNumberError}
					value={handlingUnit.unNumber ?? ""}
				/>
				{unNumberError ? <Error>Required</Error> : null}
			</div>
			<div
				className={
					"freight-hazmat-field freight-hazmat-field-proper-shipping-name"
				}
			>
				<Label editorId={`proper-shipping-name-${handlingUnit.id}`}>
					Proper Shipping Name
					<RequiredMark />
				</Label>
				<Input
					id={`proper-shipping-name-${handlingUnit.id}`}
					onChange={({ value }: { value: string }) => {
						onChange("properShippingName", value);
					}}
					valid={!properShippingNameError}
					value={handlingUnit.properShippingName ?? ""}
				/>
				{properShippingNameError ? <Error>Required</Error> : null}
			</div>
			<div className={"freight-hazmat-field freight-hazmat-field-hazmat-class"}>
				<Label editorId={`hazmat-class-${handlingUnit.id}`}>
					Hazmat Class
					<RequiredMark />
				</Label>
				<DropDownList
					data={hazmatClasses}
					dataItemKey={"id"}
					id={`hazmat-class-${handlingUnit.id}`}
					onChange={({ value }: { value: HazmatClass }) => {
						onChange("hazmatClass", value);
					}}
					textField={"code"}
					valid={!hazmatClassError}
					value={
						handlingUnit.hazmatClass ?? { id: 0, name: "select", code: "" }
					}
				/>
				{hazmatClassError ? <Error>Required</Error> : null}
			</div>
			<div
				className={"freight-hazmat-field freight-hazmat-field-packing-group"}
			>
				<Label editorId={`packing-group-${handlingUnit.id}`}>
					Packing Group
					<RequiredMark />
				</Label>
				<DropDownList
					data={displayHazmatGroups}
					dataItemKey={"id"}
					id={`packing-group-${handlingUnit.id}`}
					onChange={(event: DropDownListChangeEvent) => {
						onChange("packingGroupId", event.value as HazmatGroup);
					}}
					textField={"displayCode"}
					valid={!packingGroupError}
					value={
						handlingUnit.packingGroupId ?? {
							id: 0,
							name: "select",
							code: "",
							displayCode: "",
						}
					}
				/>
				{packingGroupError ? <Error>Required</Error> : null}
			</div>
		</div>
	);
};
